import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AlertTriangle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { requestMyAccountantRequestCorrection } from "@/services/userAccountantRequestsService";
import { requestMyNotarialRequestCorrection } from "@/services/userNotarialRequestsService";

/** Matches the server's floor — "está mal" gives the professional nothing to act on. */
const MIN_REASON_LENGTH = 10;
const MAX_REASON_LENGTH = 2000;

export interface DocumentCorrectionCardProps {
  requestId: string;
  requestSource: "notarial" | "accountant";
  /** A correction already asked for and not yet answered. */
  correctionOpen: boolean;
  correctionReason: string | null;
  /**
   * Whether asking is possible at all. False for a physically-delivered
   * document, which no upload can fix, and before anything is delivered.
   */
  canRequestCorrection: boolean;
  onCorrectionRequested: () => void;
}

/**
 * The requester's way to say the delivered document is wrong.
 *
 * Two states in one card rather than two components, because they are the
 * same subject and only one is ever true: either a correction is pending, or
 * one can be asked for. A card that showed the button while a request was
 * already open would invite the duplicate the server refuses.
 */
export function DocumentCorrectionCard({
  requestId,
  requestSource,
  correctionOpen,
  correctionReason,
  canRequestCorrection,
  onCorrectionRequested,
}: DocumentCorrectionCardProps): JSX.Element | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  const requestCorrection = useMutation({
    mutationFn: () =>
      requestSource === "accountant"
        ? requestMyAccountantRequestCorrection(requestId, reason)
        : requestMyNotarialRequestCorrection(requestId, reason),
    onSuccess: () => {
      toast.success(t("requests.user.correctionRequestedToast"));
      setOpen(false);
      setReason("");
      onCorrectionRequested();
    },
    onError: (err: unknown) => {
      // The server refuses for reasons the requester can act on — contact
      // details in the text, a correction already open — so its message is
      // shown rather than a generic failure.
      // `??` alone would let an empty array or empty string through and render
      // a blank toast, so this falls back on anything non-substantive.
      const raw = (err as { response?: { data?: { message?: string | string[] } } })
        ?.response?.data?.message;
      const joined = Array.isArray(raw) ? raw.join(" ") : raw;
      toast.error(joined?.trim() || t("requests.user.correctionRequestError"));
    },
  });

  const trimmedLength = reason.trim().length;
  const canSubmit = trimmedLength >= MIN_REASON_LENGTH && trimmedLength <= MAX_REASON_LENGTH;

  if (correctionOpen) {
    return (
      <Card className="border-amber-500/40 bg-amber-50/60 dark:bg-amber-900/10">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            {t("requests.user.correctionPendingTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {t("requests.user.correctionPendingDescription")}
          </p>
          {correctionReason ? (
            <blockquote className="border-l-2 border-amber-500 px-3 py-1 text-sm text-foreground">
              {correctionReason}
            </blockquote>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  if (!canRequestCorrection) return null;

  return (
    <>
      {/* Secondary by design: the primary action on a delivered request is
          downloading the document, not disputing it. */}
      <div className="flex justify-end">
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
          {t("requests.user.requestCorrectionCta")}
        </Button>
      </div>

      {/* Closing discards the draft. Reopening with abandoned text still in
          the box invites resubmitting something the requester walked away
          from. */}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setReason("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("requests.user.requestCorrectionTitle")}</DialogTitle>
            <DialogDescription>
              {t("requests.user.requestCorrectionDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="correction-reason">
              {t("requests.user.requestCorrectionReasonLabel")}
            </Label>
            <Textarea
              id="correction-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("requests.user.requestCorrectionReasonPlaceholder")}
              rows={4}
              maxLength={MAX_REASON_LENGTH}
              disabled={requestCorrection.isPending}
            />
            <p className="text-xs text-muted-foreground">
              {t("requests.user.requestCorrectionReasonHint")}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setOpen(false);
                setReason("");
              }}
              disabled={requestCorrection.isPending}
            >
              {t("pages.common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => requestCorrection.mutate()}
              disabled={!canSubmit || requestCorrection.isPending}
            >
              {requestCorrection.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("requests.user.requestCorrectionSubmitting")}
                </>
              ) : (
                t("requests.user.requestCorrectionSubmit")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
